import { useCallback, useEffect, useRef, useState } from 'react';
import { getBureauAlarmsByCategory } from '../services/bureauManagement/bureauManagement.service';
import type { BureauAlarm } from '../services/bureauManagement/bureauManagement.types';

export function useBureauCategory(category: string, pageSize = 50, fetchAll = false, enabled = true) {
  const [data, setData] = useState<BureauAlarm[]>([]);
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [allData, setAllData] = useState<BureauAlarm[] | undefined>(undefined);
  // Start as true so consumers show a spinner immediately rather than briefly flashing "No Data"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | undefined>(undefined);

  // Tracks the current fetch so stale responses from previous categories are ignored.
  const fetchIdRef = useRef(0);

  const fetchPage = useCallback(async (
    p = 1,
    sortOverride?: { sortBy?: string; sortDirection?: 'asc' | 'desc' }
  ) => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const effectiveSortBy = sortOverride ? sortOverride.sortBy : sortBy;
      const effectiveSortDirection = sortOverride ? sortOverride.sortDirection : sortDirection;
      const res = await getBureauAlarmsByCategory(category, p, pageSize, fetchAll, {
        sortBy: effectiveSortBy,
        sortDirection: effectiveSortDirection,
      });
      // Discard response if a newer fetch has started (category changed mid-flight).
      if (fetchId !== fetchIdRef.current) { return; }
      if (res?.success && res.data) {
        const d = res.data;
        // Backend returns 'alarms' not 'bureauAlarms'
        const alarms = d.alarms || d.bureauAlarms || [];
        const total = d.totalRecords ?? d.totalCount ?? 0;
        setData(alarms);
        const allAlarms = d.alarmsAll || d.bureauAlarmsAll;
        if (allAlarms) setAllData(allAlarms);
        setTotalPages(d.totalPages || 1);
        setPage(d.page || p);
        setTotalCount(total);
        setCounts(d.categoryCounts || {});
      } else {
        const errorMsg = res?.error?.message || 'Failed to load bureau alarms';
        setError(errorMsg);
      }
    } catch (err: unknown) {
      if (fetchId !== fetchIdRef.current) { return; }
      const errorMsg = err instanceof Error ? err.message : String(err);
      setError(errorMsg);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [category, pageSize, fetchAll, sortBy, sortDirection]);

  useEffect(() => {
    if (!enabled) { return; }
    fetchPage(1);
  }, [category, fetchPage, fetchAll, enabled]);

  const refresh = useCallback(() => fetchPage(page), [fetchPage, page]);
  const goToPage = (p: number) => fetchPage(p);
  const applySort = useCallback((nextSortBy?: string, nextSortDirection?: 'asc' | 'desc') => {
    setSortBy(nextSortBy);
    setSortDirection(nextSortDirection);
    void fetchPage(1, {
      sortBy: nextSortBy,
      sortDirection: nextSortDirection,
    });
  }, [fetchPage]);

  return {
    data,
    allData,
    loading,
    error,
    refresh,
    page,
    totalPages,
    totalCount,
    counts,
    sortBy,
    sortDirection,
    goToPage,
    applySort,
  };
}
